import _, { cloneDeep } from 'lodash';
import {
    convertNullableDateTimeToUkgUserDateTime,
    applyUkgUserTimeZoneTransformation,
    // eslint-disable-next-line import/no-unresolved
} from '@commonClient2/services/ukgDateTime';
import formatWorkplanSchedulePatternResponse from '../../store/helpers';
import { BidProfileStatus, BidProfileEditTab, EmployeeSubmissionTimeUnit } from '../../../Enums';
import TimeService from '../../../services/TimeService';

/**
 * @type {BidProfile.IScheduleTemplateItem}
 */
const defaultScheduleTemplateItem = {
    id: -1,
    orgJobPath: '',
    fullOrgJobPath: '',
    orgJobNodeId: -1,
    workplanId: undefined,
    workplanName: undefined,
    isSelected: false,
    schedulePatternTemplate: {
        id: -1,
        name: '',
        dayCount: -1,
        items: [],
    },
    nbWeek: -1,
    maxDayPerWeek: {},
};

/**
 * @param {BidProfileStatus} status
 * @param {boolean} isRebid
 * @returns {boolean}
 */
const isBaseInfoEditable = (status, isRebid) => {
    if (isRebid) {
        return false;
    }
    switch (status) {
        case BidProfileStatus.Active:
        case BidProfileStatus.Closed:
        case BidProfileStatus.Published:
        case BidProfileStatus.Canceled:
            return false;
        case BidProfileStatus.Draft:
        case BidProfileStatus.Scheduled:
        default:
            return true;
    }
};
/**
 * @param {BidProfileStatus} status
 * @param {ManagerAppData.IManagerBidSetup[]} bidSetups
 * @param {number} bidSetupId
 * @returns {boolean}
 */
const isEmployeeSelectionEditable = (status, bidSetups, bidSetupId) => {
    switch (status) {
        case BidProfileStatus.Active:
        case BidProfileStatus.Closed: {
            const setup = bidSetups.find(
                (x) => x.bidSetupId === bidSetupId,
            );
            return setup?.isEditEmployeesWhenBidActive;
        }
        case BidProfileStatus.Published:
        case BidProfileStatus.Canceled:
            return false;
        case BidProfileStatus.Draft:
        case BidProfileStatus.Scheduled:
        default:
            return true;
    }
};

/**
 * @param {BidProfileStatus} status
 * @returns {boolean}
 */
export const isScheduleSelectionEditable = (status) => [BidProfileStatus.Draft, BidProfileStatus.Scheduled].includes(status);

/**
 * @param {BidProfileStatus} status
 * @returns {boolean}
 */
const isSchedulePeriodEditable = (status) => [BidProfileStatus.Draft, BidProfileStatus.Scheduled, BidProfileStatus.Active].includes(status);

/**
 * @param {ApiManager.IEmployeeAwardingResultPresentationModel[]} awardingResult
 * @param {BidProfile.IScheduleTemplateItem[]} scheduleTemplates
 * @returns {BidProfileManagement.IEmployeeAwardingResult[]}
*/
export const simplifyAwardingResult = (awardingResult, scheduleTemplates) => awardingResult.map((x) => ({
    ...x,
    employeeId: x.employee.id,
    submittedOn: convertNullableDateTimeToUkgUserDateTime(x.submittedOn),
    hasAwardedShiftline: !!x.awardedShiftlineId,
    awardedShiftline: scheduleTemplates.find((t) => t.id === x.awardedShiftlineId) || _.cloneDeep(defaultScheduleTemplateItem),
    employeeSelectionIndex: x.employeeSelectionIndex,
}));

/**
 * @param {BidProfile.IBidProfileInformation | BiddingDraft.IDraftBidProfileInformationAccordion} bidProfileInformation
 * @param {boolean} isEditable
 * @returns {BidProfile.IBidProfileInformationAccordion}
 */
export const simplifyBidProfileInfo = (bidProfileInformation, isEditable = true) => ({
    ...bidProfileInformation,
    isExpanded: false,
    isEditable,
});

/**
 * @param {ApiManager.IScheduleTemplatesPresentationModel | ApiManager.IScheduleTemplatesDraftPresentationModel} scheduleTemplate
 * @param {boolean} isEditable
 * @returns {BidProfile.IScheduleTemplatesAccordion | BiddingDraft.IDraftScheduleTemplatesAccordion}
 */
export const simplifyScheduleTemplates = (scheduleTemplate, isEditable) => {
    /**
     * @type {BidProfile.IScheduleTemplatesAccordion | BiddingDraft.IDraftScheduleTemplatesAccordion}
     */
    const scheduleTemplates = {
        retrievedWorkplans: null,
        ...scheduleTemplate,
        isExpanded: false,
        isEditable,
        selected: [],
    };

    if (scheduleTemplate) {
        const { selectedWorkplans, workplanItems } = formatWorkplanSchedulePatternResponse(scheduleTemplate.items);
        scheduleTemplates.selected = selectedWorkplans;
        if (workplanItems.length) { // Using workplan
            scheduleTemplates.retrievedWorkplans = {
                workplans: workplanItems,
                shifts: scheduleTemplate.shifts,
                paycodes: scheduleTemplate.paycodes,
            };
            // scheduleTemplates.items = [];     // Get shiftlines from retrievedWorkplans.workplans.items!
        }
    }
    return scheduleTemplates;
};

/**
 * @param { ApiManager.IEmployeeInformationPresentationModel } employeeInfos
 * @param {{[Key: number]:string}} hyperfindDictionary
 * @param {boolean} isEditable
 * @returns {BidProfile.IEmployeeSelectionAccordion}
 */
export const simplifyEmployeeInfos = (employeeInfos, hyperfindDictionary, isEditable) => ({
    ...employeeInfos,
    isExpanded: false,
    isEditable,
    hyperfindLabel: employeeInfos.hyperfindId ? hyperfindDictionary[employeeInfos.hyperfindId] : '',
    periodSelected: {
        startDate: convertNullableDateTimeToUkgUserDateTime(employeeInfos.periodStartDate)
            || applyUkgUserTimeZoneTransformation(new Date()),
        endDate: convertNullableDateTimeToUkgUserDateTime(employeeInfos.periodEndDate)
            || applyUkgUserTimeZoneTransformation(new Date()),
    },
});

/**
 * @returns {BidProfile.IEmployeeSelectionAccordion}
 */
export const getDefaultEmployeeInfos = () => ({
    employeesIds: [],
    hyperfindId: undefined,
    rankedEmployees: [],
    isExpanded: false,
    isEditable: true,
    hyperfindLabel: '',
    periodSelected: {
        startDate: applyUkgUserTimeZoneTransformation(new Date()),
        endDate: applyUkgUserTimeZoneTransformation(new Date()),
    },
    lockedEmployeeIds: [],
});

/**
 * @param {ApiManager.ISequentialEmployeeSchedule} sequentialSchedule
 * @returns {BidProfile.ISequentialEmployeeSchedule}
 */
export const simplifySequentialSchedule = (sequentialSchedule) => {
    /**
     * @type {BidProfile.ISequentialEmployeeSchedule}
     */
    const parsedSequentialSchedule = {};
    if (sequentialSchedule) {
        for (const value of Object.values(sequentialSchedule)) {
            parsedSequentialSchedule[value.employeeId] = {
                ...value,
                employeeSubmissionEndDate: applyUkgUserTimeZoneTransformation(value.employeeSubmissionEndDate),
                employeeSubmissionStartDate: applyUkgUserTimeZoneTransformation(value.employeeSubmissionStartDate),
            };
        }
    }
    return parsedSequentialSchedule;
};

/**
 * @param { ApiManager.ISchedulingAndVisibilityPresentationModel } schedulingAndVisibility
 * @param { boolean } isEditable
 * @returns {BidProfile.IBidProfileSchedulingAccordion}
 */
export const simplifySchedulingAndVisibility = (schedulingAndVisibility, isEditable) => ({
    ...schedulingAndVisibility,
    submissionPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(schedulingAndVisibility?.submissionPeriodStartDate),
    submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(schedulingAndVisibility?.submissionPeriodEndDate),
    requestPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(schedulingAndVisibility?.requestPeriodStartDate),
    requestPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(schedulingAndVisibility?.requestPeriodEndDate),
    dayOfTheWeek: schedulingAndVisibility.dayOfTheWeek.length
        ? Array.from(Array(7).keys()).reduce((/** @type {BidProfile.IDayOfWeekDictionary} */prev, curr) => {
            prev[curr] = schedulingAndVisibility.dayOfTheWeek.includes(curr);
            return prev;
        }, {})
        : {
            0: false, 1: true, 2: true, 3: true, 4: true, 5: true, 6: false,
        },
    startBiddingTime: schedulingAndVisibility.startBiddingTime
        ? applyUkgUserTimeZoneTransformation(schedulingAndVisibility.startBiddingTime)
        : TimeService.getDateTimeFromMinutes(420),
    endBiddingTime: schedulingAndVisibility.endBiddingTime
        ? applyUkgUserTimeZoneTransformation(schedulingAndVisibility.endBiddingTime)
        : TimeService.getDateTimeFromMinutes(960),
    timePerEmployeeUnit: schedulingAndVisibility.timePerEmployeeUnit || EmployeeSubmissionTimeUnit.Minute, // Minutes by default
    timePerEmployeeValue: schedulingAndVisibility.timePerEmployeeValue || 1,
    isAnytime: schedulingAndVisibility.isAnytime || false,
    isExpanded: false,
    sequentialSchedule: simplifySequentialSchedule(schedulingAndVisibility.sequentialSchedule),
    isEditable,
});

/**
 * @param {ApiManager.IRebidParentPresentationModel[] | null} rebidParents
 * @param {{
 *  isManagementAllowed: boolean,
 *  isCreationAllowed: boolean,
 *  isViewAllowed: boolean,
 * }} inheritedData
 * @param {{[Key: number]:string}} hyperfindDictionary
 * @returns {BidProfile.IBidProfileRebid[] | null}
 */
export const simplifyRebidParents = (rebidParents, inheritedData, hyperfindDictionary) => {
    if (!rebidParents) {
        return null;
    }
    return rebidParents.map((x) => ({
        ...inheritedData,
        id: x.profileId,
        status: x.status,
        name: x.name,
        description: x.description,
        hyperfindLabel: hyperfindDictionary[x.hyperfindId],
        isOriginalBid: x.isOriginalBid,
        bidProcessType: x.bidProcessType,
        scheduleSource: x.scheduleSource,
        requestPeriodStartDate: x.requestPeriodStartDate,
        requestPeriodEndDate: x.requestPeriodEndDate,
        submissionPeriodStartDate: x.submissionPeriodStartDate,
        submissionPeriodEndDate: x.submissionPeriodEndDate,
        isRebidParent: true,
        rebidNumber: x.rebidNumber,
        isForeverRequestPeriod: x.isForeverRequestPeriod,
    }));
};

/**
 * @param {string} id
 * @param {ApiManager.IBidProfileDraftPresentationModel | ApiManager.IBidProfilePresentationModel} base
 * @param {{
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 * @param {{[Key: number]:string}} hyperfindDictionary
 * @param {BidProfileStatus} status
 * @param {BidProfileEditTab} tabSelected
 * @returns {BidProfile.IBaseProfile}
 */
const simplifyBaseBidProfile = (id, base, storeData, hyperfindDictionary, status, tabSelected) => {
    const canEditBaseInfo = isBaseInfoEditable(status, base.isRebid);
    const canEditEmployeeSelection = isEmployeeSelectionEditable(
        status,
        storeData.managerBidSetups,
        base.bidProfileInformation.bidSetupId,
    );

    const canEditSchedulePeriod = isSchedulePeriodEditable(status);
    const bidProfileInfo = simplifyBidProfileInfo(base.bidProfileInformation, canEditBaseInfo);
    const employeesInfos = simplifyEmployeeInfos(base.employeeInfos, hyperfindDictionary, canEditEmployeeSelection);
    const schedulingAndVisibility = simplifySchedulingAndVisibility(base.schedulingAndVisibility, canEditSchedulePeriod);
    // @ts-ignore - ScheduleTemplates are set in parallel in a separate middleware
    return {
        id,
        lastUpdate: convertNullableDateTimeToUkgUserDateTime(base.lastUpdate),
        creationDate: convertNullableDateTimeToUkgUserDateTime(base.creationDate),
        anchorDay: base.anchorDay,
        tabSelected,
        bidProfileInformation: bidProfileInfo,
        originalBidProfileInformation: cloneDeep(bidProfileInfo),
        employees: employeesInfos,
        originalEmployees: cloneDeep(employeesInfos),
        schedule: schedulingAndVisibility,
        originalSchedule: cloneDeep(schedulingAndVisibility),
        maxScheduleRowItems: base.maxScheduleRowItems,
        isRebidAllowed: base.isRebidAllowed,
        isRebid: base.isRebid,
        rebidNumber: base.rebidNumber,
        rebidParents: simplifyRebidParents(
            base.rebidParents,
            { 
                isCreationAllowed: base.isCreationAllowed, 
                isManagementAllowed: base.isManagementAllowed,
                isViewAllowed: base.isViewAllowed
            },
            hyperfindDictionary,
        ),
        isReImportNeeded: false,
        isWorkPlansRetrieved: false,
        status: -2, // Will be overwritten outside of this fct
        managerWarnings: base.managerWarnings,
        isForeverRequestPeriod: base.schedulingAndVisibility.isForeverRequestPeriod,
        originalRequestPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(base.originalRequestPeriodEndDate),
        originalRequestPeriodEndDateIsForever: base.originalRequestPeriodEndDateIsForever,
        isCreationAllowed: base.isCreationAllowed, 
        isManagementAllowed: base.isManagementAllowed,
        isViewAllowed: base.isViewAllowed
    };
};

/**
 * @param {ApiManager.IBidProfileDraftPresentationModel[]} drafts
 * @param {{
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 * @param {BidProfileEditTab} tabSelected
 * @returns {BiddingDraft.IBidProfileDraft[]}//simplified = no schedule patterns
 */
export const simplifyBidProfileDraftModels = (drafts, storeData, tabSelected = BidProfileEditTab.Form) => {
    const hyperfindDictionary = Object.assign({}, ...storeData.hyperfinds.map((x) => ({ [x.value]: x.label })));
    const result = drafts.map((draft) => {
        const baseBidProfile = simplifyBaseBidProfile(
            draft.draftId,
            draft,
            storeData,
            hyperfindDictionary,
            BidProfileStatus.Draft,
            tabSelected,
        );

        return {
            ...baseBidProfile,
            draftId: draft.draftId,
            status: BidProfileStatus.Draft,
            managerId: draft.managerId,
            // @ts-ignore
            anchorDay: draft.anchorDay,
            // @ts-ignore
            isCompleteDraftActionProcessing: false,
            parentRequestPeriodStartDate: convertNullableDateTimeToUkgUserDateTime(draft.parentRequestPeriodStartDate),
            isUploadingSchedule: draft.isUploadingSchedule,
        };
    });
    return result;
};

/**
 * @param {ApiManager.IBidProfilePresentationModel[]} profilesResponse
 * @param {{
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 * @param {BidProfileEditTab} tabSelected
 * @returns {BidProfile.IBidProfile[]}//simplified = no schedule patterns
 */
export const simplifyBidProfileModels = (profilesResponse, storeData, tabSelected = BidProfileEditTab.Form, isArchive = false) => {
    const hyperfindDictionary = Object.assign({}, ...storeData.hyperfinds.map((x) => ({ [x.value]: x.label })));
    const result = profilesResponse.map((x) => {
        const baseBidProfile = simplifyBaseBidProfile(x.profileId, x, storeData, hyperfindDictionary, x.status, tabSelected);

        return {
            ...baseBidProfile,
            profileId: x.profileId,
            // @ts-ignore
            status: x.status,
            // @ts-ignore
            anchorDay: x.anchorDay,
            isCreationAllowed: x.isCreationAllowed,
            isManagementAllowed: x.isManagementAllowed,
            isViewAllowed: x.isViewAllowed,
            isPublishingToWorkplan: x.isPublishingToWorkplan,
            isUploadingSchedule: x.isUploadingSchedule,
            isArchive
        };
    });
    return result;
};
